/**
 * Navigateur de serveurs : toutes les parties visibles (publiques, d'amis, les miennes et
 * celles où l'on est invité), filtres, recherche, et la fiche de la partie choisie.
 * La liste se recharge quand le serveur signale un changement (aucun sondage).
 */
import type { ComponentChildren } from 'preact';
import { useEffect } from 'preact/hooks';
import {
  Ban,
  Copy,
  Crown,
  KeyRound,
  Link,
  Mail,
  Mountain,
  Play,
  Plus,
  RefreshCw,
  Search,
  Server,
  Settings,
  Store,
  Trash2,
  Truck,
  UserPlus,
  Users,
  type LucideIcon,
} from 'lucide-preact';
import type { PosteRole, ServerSummary } from '@snowchain/shared';
import { lobby } from './lobby';
import { menu, type ServerFilter } from './state';
import { act, copyCode, copyInvite, join, joinState } from './actions';
import { Avatar, AvatarStack, Btn, Empty, LiveTag, Segmented, Spinner, Stars, VisibilityTag, gameDate, initials, relTime, toneOf } from './ui';

const POSTE: Record<PosteRole, { label: string; icon: LucideIcon }> = {
  station: { label: 'Station de ski', icon: Mountain },
  supermarche: { label: 'Supermarché', icon: Store },
  transport: { label: 'Transport', icon: Truck },
};

function matches(s: ServerSummary, f: ServerFilter, q: string, onlyOpen: boolean, friendIds: Set<string>): boolean {
  if (f === 'friends' && !s.friends.length && !(s.owner && friendIds.has(s.owner.id))) return false;
  if (f === 'mine' && !s.myRole) return false;
  if (f === 'invites' && !s.invitedBy) return false;
  if (onlyOpen && s.online.length >= s.maxPlayers) return false;
  if (q) {
    const hay = `${s.name} ${s.stationName} ${s.owner?.name ?? ''}`.toLowerCase();
    if (!hay.includes(q)) return false;
  }
  return true;
}

function rank(s: ServerSummary, friendIds: Set<string>): number[] {
  const friendsOnline = s.online.filter((p) => friendIds.has(p.id)).length;
  return [s.invitedBy ? 0 : 1, -friendsOnline, s.live ? 0 : 1, -s.online.length, -(s.lastPlayedAt ?? 0), -s.savedAt];
}

function byRank(a: number[], b: number[]): number {
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return (a[i] as number) - (b[i] as number);
  return 0;
}

function ServerRow(props: { s: ServerSummary; selected: boolean; friendIds: Set<string> }) {
  const s = props.s;
  const d = gameDate(s.gameTime);
  const meId = lobby.me.value?.id;
  const st = joinState(s);
  return (
    <div
      class={`mn-tr${props.selected ? ' on' : ''}`}
      role="row"
      tabIndex={0}
      onClick={() => (menu.selected.value = s.id)}
      onDblClick={() => join(s)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') join(s);
        if (e.key === ' ') {
          e.preventDefault();
          menu.selected.value = s.id;
        }
      }}
    >
      <span class="mn-td mn-td-name" role="cell">
        <span class="mn-wrow-crest" style={{ background: toneOf(s.id) }} aria-hidden="true">
          {initials(s.stationName || s.name)}
        </span>
        <span class="mn-td-text">
          <span class="mn-td-title">
            {s.stationName || s.name}
            {s.invitedBy && (
              <span class="mn-pill mn-pill-ember" title={`${s.invitedBy.name} vous invite`}>
                <Mail size={11} /> Invitation
              </span>
            )}
            {s.myRole === 'station' && (
              <span class="mn-pill" title="Vous gérez cette station">
                <Crown size={11} /> Ma station
              </span>
            )}
          </span>
          <span class="mn-td-sub">
            <VisibilityTag v={s.visibility} /> {s.name}
            {s.owner && s.owner.id !== meId && <span class="mn-td-host"> · par {s.owner.name}</span>}
          </span>
        </span>
      </span>
      <span class="mn-td mn-td-players" role="cell">
        <span class={`mn-live-dot${s.live ? ' on' : ''}`} title={s.live ? 'En ligne : le temps avance' : 'En veille : le temps est arrêté'} />
        <span class={`num${s.online.length >= s.maxPlayers ? ' mn-full' : ''}`}>
          {s.online.length}/{s.maxPlayers}
        </span>
        <AvatarStack people={s.online.filter((p) => props.friendIds.has(p.id))} max={3} size={20} />
      </span>
      <span class="mn-td mn-td-day" role="cell">
        <span>Jour {d.day}</span>
        <span class="mn-td-sub">{d.label}</span>
      </span>
      <span class="mn-td mn-td-go" role="cell">
        <Btn
          kind={props.selected ? 'primary' : 'secondary'}
          size="sm"
          icon={Play}
          disabled={!!st.blocked || menu.joining.value === s.id}
          title={st.blocked ?? st.label}
          onClick={(e) => {
            e.stopPropagation();
            join(s);
          }}
        >
          {st.blocked ? 'Complet' : 'Jouer'}
        </Btn>
      </span>
    </div>
  );
}

function Stat(props: { label: string; title?: string; children: ComponentChildren }) {
  return (
    <div class="mn-stat" title={props.title}>
      <span class="mn-stat-label">{props.label}</span>
      <span class="mn-stat-value">{props.children}</span>
    </div>
  );
}

export function ServerDetail(props: { s: ServerSummary }) {
  const s = props.s;
  const me = lobby.me.value;
  const d = gameDate(s.gameTime);
  const st = joinState(s);
  const owner = !!me && s.owner?.id === me.id;
  const friendIds = new Set(lobby.social.value.friends.map((f) => f.id));
  const busy = menu.joining.value === s.id;
  const offlineFriends = s.friends.filter((f) => !s.online.some((p) => p.id === f.id));
  return (
    <aside class="mn-detail" aria-label={`Partie ${s.name}`}>
      <div class="mn-detail-scroll">
        <div class="mn-detail-head">
          <span class="mn-detail-crest" style={{ background: toneOf(s.id) }} aria-hidden="true">
            {initials(s.stationName || s.name)}
          </span>
          <div class="mn-detail-titles">
            <h2>{s.stationName || s.name}</h2>
            <div class="mn-detail-sub">
              {s.name} · <VisibilityTag v={s.visibility} /> <LiveTag live={s.live} />
            </div>
          </div>
        </div>
        {s.invitedBy && (
          <div class="mn-note mn-note-ember">
            <Mail size={14} /> <span>
              <b>{s.invitedBy.name}</b> vous invite sur cette partie.
            </span>
          </div>
        )}
        <div class="mn-detail-host">
          {s.owner ? (
            <>
              <Avatar id={s.owner.id} name={s.owner.name} size={28} />
              <span>
                <span class="mn-muted">Hôte</span> <b>{owner ? 'Vous' : s.owner.name}</b>
              </span>
            </>
          ) : (
            <span class="mn-muted">Station libre : le premier joueur la prend.</span>
          )}
          <span class="mn-detail-day">
            Jour {d.day} · {d.label}
          </span>
        </div>
        <div class="mn-stats">
          <Stat label="Réputation">
            <Stars value={s.reputation * 5} size={13} />
          </Stat>
          <Stat label="Satisfaction">{s.satisfaction} %</Stat>
          <Stat label="Forfaits" title="Forfaits vendus aujourd’hui">
            {s.visitorsToday.toLocaleString('fr-FR')}
          </Stat>
          <Stat label="Domaine">{s.openKm.toLocaleString('fr-FR')} km</Stat>
          <Stat label="Remontées">{s.lifts}</Stat>
          <Stat label="Pistes">{s.pistes}</Stat>
        </div>
        <h4 class="mn-h4">Postes de la vallée</h4>
        <ul class="mn-postes">
          {s.postes.map((p) => {
            const P = POSTE[p.role];
            return (
              <li key={p.role}>
                <span class="mn-poste-ic">
                  <P.icon size={14} />
                </span>
                <span class="mn-poste-text">
                  <span>{p.name}</span>
                  <span class="mn-muted">{P.label}</span>
                </span>
                <span class={`mn-pill${p.controller === 'ai' ? '' : ' mn-pill-glacier'}`}>{p.controller === 'ai' ? 'IA' : (p.playerName ?? 'Joueur')}</span>
              </li>
            );
          })}
        </ul>
        <h4 class="mn-h4">
          Connectés · {s.online.length}/{s.maxPlayers}
        </h4>
        {s.online.length ? (
          <ul class="mn-people">
            {s.online.map((p) => (
              <li key={p.id}>
                <Avatar id={p.id} name={p.name} size={24} presence="game" />
                <span>{p.id === me?.id ? `${p.name} (vous)` : p.name}</span>
                {friendIds.has(p.id) && <span class="mn-pill mn-pill-pine">Ami</span>}
                {s.owner?.id === p.id && <Crown size={13} class="mn-gold" />}
              </li>
            ))}
          </ul>
        ) : (
          <p class="mn-muted mn-small">Personne pour l’instant : le temps reprendra à votre arrivée.</p>
        )}
        {offlineFriends.length > 0 && (
          <p class="mn-small mn-muted">
            Amis qui y ont joué : {offlineFriends.map((f) => f.name).join(', ')}
          </p>
        )}
        {owner && (
          <div class="mn-row-btns">
            <Btn kind="ghost" icon={Settings} onClick={() => (menu.dialog.value = { k: 'settings', server: s })}>
              Paramètres
            </Btn>
            <Btn kind="ghost" icon={Trash2} class="mn-btn-dangerghost" onClick={() => (menu.dialog.value = { k: 'delete', server: s })}>
              Supprimer
            </Btn>
          </div>
        )}
        {!owner && s.myRole && (
          <Btn
            kind="ghost"
            icon={Ban}
            onClick={async () => {
              if (await act({ op: 'world.leave', id: s.id }, 'Partie retirée', `« ${s.name} » n’apparaît plus dans vos parties.`)) void lobby.loadServers();
            }}
          >
            Retirer de mes parties
          </Btn>
        )}
        <p class="mn-small mn-muted">
          Créée {relTime(s.createdAt)} · {s.members > 1 ? `${s.members} joueurs y ont joué` : `${s.members} joueur y a joué`}
        </p>
      </div>
      <div class="mn-detail-actions">
        <Btn kind="primary" size="lg" icon={Play} full disabled={!!st.blocked || busy} title={st.blocked ?? undefined} onClick={() => join(s)}>
          {busy ? 'Connexion...' : st.label}
        </Btn>
        <div class="mn-row-btns">
          {s.myRole && (
            <Btn icon={UserPlus} onClick={() => (menu.dialog.value = { k: 'invite', server: s })}>
              Inviter
            </Btn>
          )}
          <Btn icon={Link} onClick={() => void copyInvite(s.id)}>
            Lien
          </Btn>
          <Btn icon={Copy} title="Copier le code de la partie" onClick={() => void copyCode(s.id)}>
            {s.id}
          </Btn>
        </div>
      </div>
    </aside>
  );
}

export function ServersView() {
  useEffect(() => lobby.watchServers(), []);
  const all = lobby.servers.value;
  const f = menu.filter.value;
  const q = menu.search.value.trim().toLowerCase();
  const friendIds = new Set(lobby.social.value.friends.map((x) => x.id));
  const list = (all ?? []).filter((s) => matches(s, f, q, menu.onlyOpen.value, friendIds)).sort((a, b) => byRank(rank(a, friendIds), rank(b, friendIds)));
  const sel = (all ?? []).find((s) => s.id === menu.selected.value) ?? list[0] ?? null;
  const count = (k: ServerFilter) => (all ?? []).filter((s) => matches(s, k, '', false, friendIds)).length;
  const players = (all ?? []).reduce((n, s) => n + s.online.length, 0);
  return (
    <div class="mn-servers">
      <header class="mn-page-head">
        <div>
          <h1>Serveurs</h1>
          <p class="mn-muted">
            {all ? `${all.length} partie${all.length > 1 ? 's' : ''} visible${all.length > 1 ? 's' : ''} · ${players} joueur${players > 1 ? 's' : ''} en partie` : 'Chargement...'}
          </p>
        </div>
        <div class="mn-page-actions">
          <Btn icon={KeyRound} onClick={() => (menu.dialog.value = { k: 'code' })}>
            Code
          </Btn>
          <Btn kind="primary" icon={Plus} onClick={() => (menu.dialog.value = { k: 'create' })}>
            Nouvelle partie
          </Btn>
        </div>
      </header>
      <div class="mn-toolbar">
        <Segmented
          label="Filtre"
          value={f}
          onChange={(v) => (menu.filter.value = v)}
          options={[
            { value: 'all', label: `Toutes · ${count('all')}` },
            { value: 'friends', label: `Amis · ${count('friends')}` },
            { value: 'mine', label: `Mes parties · ${count('mine')}` },
            { value: 'invites', label: `Invitations · ${count('invites')}` },
          ]}
        />
        <label class="mn-search">
          <Search size={15} />
          <input type="search" placeholder="Partie, station ou hôte" value={menu.search.value} onInput={(e) => (menu.search.value = (e.target as HTMLInputElement).value)} aria-label="Rechercher une partie" />
        </label>
        <label class="mn-check">
          <input type="checkbox" checked={menu.onlyOpen.value} onChange={(e) => (menu.onlyOpen.value = (e.target as HTMLInputElement).checked)} />
          Places libres
        </label>
        <span class="mn-toolbar-end">
          <span class="mn-muted mn-small">{lobby.serversAt.value ? `à jour ${relTime(lobby.serversAt.value)}` : ''}</span>
          <Btn kind="ghost" size="sm" icon={RefreshCw} title="Actualiser la liste" disabled={lobby.serversLoading.value} onClick={() => void lobby.loadServers()} />
        </span>
      </div>
      <div class="mn-servers-body">
        <div class="mn-table" role="table" aria-label="Parties">
          <div class="mn-tr mn-th" role="row">
            <span class="mn-td" role="columnheader">
              Partie
            </span>
            <span class="mn-td" role="columnheader">
              Joueurs
            </span>
            <span class="mn-td" role="columnheader">
              Calendrier
            </span>
            <span class="mn-td" role="columnheader" />
          </div>
          <div class="mn-tbody">
            {all === null ? (
              <div class="mn-table-msg">
                <Spinner /> Chargement des parties...
              </div>
            ) : list.length ? (
              list.map((s) => <ServerRow key={s.id} s={s} selected={sel?.id === s.id} friendIds={friendIds} />)
            ) : (
              <Empty icon={f === 'friends' ? Users : Server} title={f === 'invites' ? 'Aucune invitation en attente' : f === 'friends' ? 'Aucune partie d’amis visible' : q ? 'Aucune partie ne correspond' : 'Aucune partie ici pour l’instant'}>
                {f === 'friends' ? (
                  <Btn size="sm" icon={UserPlus} onClick={() => (menu.view.value = 'friends')}>
                    Ajouter des amis
                  </Btn>
                ) : (
                  <Btn size="sm" kind="primary" icon={Plus} onClick={() => (menu.dialog.value = { k: 'create' })}>
                    Créer une partie
                  </Btn>
                )}
              </Empty>
            )}
          </div>
        </div>
        {sel && <ServerDetail s={sel} />}
      </div>
    </div>
  );
}
